/**
 * Marquee — seamless infinite scroll driven by JavaScript.
 *
 * Usage:
 *   <div class="c-banner__track" data-marquee-speed="80">
 *     <span class="c-banner__set"> … keywords … </span>
 *   </div>
 *
 * The single `.c-banner__set` is cloned as many times as needed to fill the
 * viewport, then the track is translated by exactly one set-width before
 * resetting — giving a perfectly seamless loop at a consistent px/s speed.
 */

const instances = [];

function startMarquee(track) {
  const set = track.querySelector(".c-banner__set");
  if (!set) return;

  // Speed in pixels per second (configurable via data attribute)
  const speed = parseFloat(track.dataset.marqueeSpeed) || 80;

  // Measure via getBoundingClientRect for accuracy
  const setWidth = set.getBoundingClientRect().width;
  if (setWidth === 0) return;

  // Clone the set enough times to guarantee no visible gap
  const clonesNeeded = Math.ceil(window.innerWidth / setWidth) + 2;
  for (let i = 0; i < clonesNeeded; i++) {
    const clone = set.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    clone.querySelectorAll("a, button, input, select, textarea, [tabindex]").forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.setAttribute("tabindex", "-1");
    });
    track.appendChild(clone);
  }

  // Animate with requestAnimationFrame
  let offset = 0;
  let lastTime = null;
  let rafId = null;

  function step(timestamp) {
    if (lastTime === null) lastTime = timestamp;
    const delta = (timestamp - lastTime) / 1000;
    lastTime = timestamp;

    offset -= speed * delta;

    // Reset when we've scrolled one full set width
    if (Math.abs(offset) >= setWidth) {
      offset += setWidth;
    }

    track.style.transform = `translateX(${offset}px)`;
    rafId = requestAnimationFrame(step);
  }

  rafId = requestAnimationFrame(step);

  // Store cleanup
  instances.push({
    track,
    destroy() {
      if (rafId) cancelAnimationFrame(rafId);
    },
  });
}

export function initMarquee() {
  const tracks = document.querySelectorAll("[data-marquee-speed]");
  if (!tracks.length) return;

  // Defer until after fonts are loaded and layout is stable
  function init() {
    tracks.forEach((track) => {
      // Skip if already initialised (prevents double-init)
      if (track.dataset.marqueeActive) return;
      track.dataset.marqueeActive = "true";
      startMarquee(track);
    });
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => requestAnimationFrame(init));
  } else {
    // Fallback for browsers without Font Loading API
    requestAnimationFrame(init);
  }
}

export function destroyMarquee() {
  instances.forEach(({ track, destroy }) => {
    destroy();
    // Remove clones (keep only the first .c-banner__set)
    const sets = track.querySelectorAll(".c-banner__set");
    sets.forEach((s, i) => {
      if (i > 0) s.remove();
    });
    track.style.transform = "";
    delete track.dataset.marqueeActive;
  });
  instances.length = 0;
}
